/**
 * Trace-flow view of a trail — the path the engineer actually walked.
 *
 * Built ONLY from the trail's own steps (`from_nid` -> `to_nid`, ordered by
 * `step_no`). It deliberately does NOT read the live diagnostic-map snapshot:
 * that only exists while a session is alive, and this same view has to work
 * when a closed trail is reviewed later at /learning/{id}.
 *
 * Rendering is a small self-contained SVG canvas (dagre for layout) rather
 * than TraversalGraph: that component is owned by the TSE dashboard, takes a
 * pre-laid-out snapshot graph, keeps node selection internal and exposes no
 * "node was clicked" callback — the one thing this view needs. So the
 * Diagnostic Map's VISUAL LANGUAGE is reused rather than its component:
 * the same palette (GNODE_STYLES), the same shapes (SHAPE_MAP), the same
 * bordered edge-condition pill, numbered corner badge, faded-and-dashed
 * treatment for anything untraversed, amber selection ring, cyan current
 * ring, and the same bottom bar of zoom % / Fit / Trace Path stepper. The
 * one deliberate departure: a branch that was never taken is drawn GREY
 * rather than in a faded version of its family colour — see nodePalette().
 *
 * What the Diagnostic Map has no equivalent of is the engineer's MARK. Since
 * colour is spoken for by the node's type, the mark hangs off the node's
 * top-left corner as a ✓ / ✗ / ? badge — see MARK_STYLE below.
 *
 * Marking is NOT forked: this component receives the very same `results` map
 * and `onMark(stepNo, value)` handler the list view gets, so a mark made here
 * is already applied when the user flips back to List, and it rides the same
 * debounced markSteps() PATCH.
 */
import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from "react";
import dagre from "@dagrejs/dagre";
import {
  List, Workflow, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Copy, Check,
} from "lucide-react";
import {
  KIND_LABEL, RESULTS, stepHeadline,
  EvidenceBranch, EvidenceBody, stepEvidence,
  RuledOutNote, stepResultNote,
} from "./TrailSteps";
import { HybridTurn } from "./HybridTrail";

/* ── what a node IS: node id -> family -> mermaid shape ──────────────────
 *
 * The id -> family rules are NOT invented here. They are a port of the
 * engine's own classifier, `_node_type_from_mermaid_id`
 * (server/app/tse_agent/tse_session.py:2971-2994) — the same function the
 * Diagnostic Map's node types come from, and the one
 * `node_family()` (server/app/learning/capture.py:295-320) deliberately
 * mirrors. Kept in the same order, because the rules overlap and the FIRST
 * match wins: WF_ZIATF_START is a Workflow, not a Symptom.
 *
 * Ported rather than imported because the trail's steps carry bare nids —
 * no parsed graph, no type field. A closed trail reviewed at /learning/{id}
 * has no live diagnostic-map snapshot to read a type off, so the id is all
 * there is.
 */
export function nodeTypeFromNid(nodeId, label = "") {
  const nid = String(nodeId || "").toUpperCase();
  const text = String(label || "").toLowerCase();
  if (!nid) return "DiagnosticStep";
  const tail = nid.slice(3);
  if (/^(WF_|MAIN_|START_)/.test(nid) || nid.includes("_START")) return "Workflow";
  if (/^(PC_|CAUSE_|POSSIBLE_CAUSE)/.test(nid) || tail.includes("_PC")) return "PossibleCause";
  if (/^(RES_|RESOLUTION_|FIX_)/.test(nid) || tail.includes("_RES") || text.includes("resolution")) return "Resolution";
  if (/^(REM_|REMED_|RX_)/.test(nid)) return "Remediation";
  if (/^(WORK_|WA_|WRK_)/.test(nid) || text.includes("workaround")) return "Workaround";
  if (/^(DS_|DIAG_|CHECK_|STEP_)/.test(nid) || tail.includes("_DS")) return "DiagnosticStep";
  if (/^(DEC_|Q_)/.test(nid) || /^Q\d+[A-Z]?$/.test(nid) || /_Q\d/.test(nid) || String(label || "").includes("?")) return "Decision";
  if (/^(PROB_|ROOT_)/.test(nid)) return "Problem";
  if (/^(SYM_|S_|CC_)/.test(nid) || nid.includes("_SYM")) return "Symptom";
  return "DiagnosticStep";
}

/* Family -> shape. Same mapping the Diagnostic Map already draws with
   (SHAPE_MAP in src/components/tse/DiagnosticMapWindow.jsx:106-118), so a
   decision is a diamond in both views. Only the families
   `_node_type_from_mermaid_id` can actually return are listed; Product /
   Info never come out of an id, so no cylinder or flag here. */
const SHAPE_FOR_TYPE = {
  Problem: "diamond",
  Decision: "diamond",
  PossibleCause: "hexagon",
  Symptom: "parallelogram",
  Workflow: "subroutine",
  Resolution: "stadium",
  Remediation: "roundedRect",
  Workaround: "trapezoid",
  DiagnosticStep: "rect",
};

/* ── the pale variant of the Diagnostic Map's palette ─────────────────────
 *
 * Family -> colour starts from GNODE_STYLES in
 * src/components/tse/DiagnosticMapWindow.jsx:6-19 — the same hexes, the same
 * icon letters — so a PossibleCause is the same violet here as it is on the
 * Diagnostic Map and an engineer reads one legend, not two. Ported rather
 * than imported because DiagnosticMapWindow keeps it module-private and
 * importing that module would pull the whole TSE map (and dagre-heavy
 * snapshot code) into the learning bundle.
 *
 * It is NOT drawn the map's saturated way, though. The map fills a node with
 * `bg` and writes white on it; that works for a 10px chip and fails here,
 * because a trail node carries a whole sentence at 12px. White on the map's
 * yellow and lime is ~1.9:1, which already forced a per-family ink override.
 *
 * So every family uses the map's OWN "not visited" recipe instead
 * (TraversalGraph.jsx:794-798): a tint of `bg`, a stroke of `border`, and the
 * label inked in the border colour. Two tunings on top of it:
 *
 *   fill  bg + NODE_FILL_A ("33", 20%) — 4D/30% is the map's value and it is
 *         right for a colour chip, but it darkens the ground a text-bearing
 *         box needs; 20% keeps the hue obvious and the ink readable.
 *   ink   shade(border, INK_DARKEN) — the border colour ALONE is nowhere near
 *         legible on its own tint (1.79:1 at worst). Multiplying its channels
 *         preserves hue and saturation and only drops value, so orange inks
 *         as brown and sky as navy — the same colour, read as text. 0.58 is
 *         the factor at which the WORST family clears 4.5:1 (see below).
 *
 * Resulting label contrast, ink on its own 20% tint:
 *   Workflow 8.91 · Resolution 8.70 · PossibleCause 8.25 · Problem 7.65 ·
 *   Decision 7.20 · Remediation 6.91 · Symptom 6.58 · Workaround 6.30 ·
 *   DiagnosticStep 6.16 · _jump 4.75 (worst)
 * A node with no step of its own is drawn on half that tint with its ink at
 * GHOST_TEXT_OP, which is the real floor of the canvas: grey on grey (a
 * ghost) is 7.25, and the worst family a walk can pass through —
 * DiagnosticStep — is 4.66. Nothing drops under 4.5.
 * The DiagnosticStep / Workaround special case the saturated pass needed is
 * gone: nothing is hand-picked any more, every ink is a function of `border`.
 */
const NODE_FILL_A = "33";   // 20% — a walked node
const GHOST_FILL_A = "1A";  // 10% — no step of its own (ghost or passed through)
const GHOST_STROKE_A = "99";// 60% — the map's own dimmed stroke alpha
const GHOST_TEXT_OP = 0.85; // recessed, still >=4.6:1 on the tint underneath
const INK_DARKEN = 0.58;

/** Multiply-darken a hex: same hue, same saturation, lower value. */
export function shade(hex, f = INK_DARKEN) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  if (!m) return "#1e293b";
  const n = parseInt(m[1], 16);
  const ch = (shift) => Math.max(0, Math.min(255, Math.round(((n >> shift) & 255) * f)));
  return `#${[16, 8, 0].map((s) => ch(s).toString(16).padStart(2, "0")).join("")}`;
}

const family = (bg, border, icon) => ({ bg, border, text: shade(border), icon });

const TYPE_STYLE = {
  Problem:        family("#ef4444", "#dc2626", "!"),
  Symptom:        family("#f97316", "#ea580c", "S"),
  Workflow:       family("#6366f1", "#4f46e5", "W"),
  Decision:       family("#0ea5e9", "#0284c7", "?"),
  DiagnosticStep: family("#eab308", "#ca8a04", "D"),
  PossibleCause:  family("#a855f7", "#9333ea", "C"),
  Resolution:     family("#60a5fa", "#2563eb", "R"),
  Remediation:    family("#14b8a6", "#0d9488", "Rx"),
  Workaround:     family("#84cc16", "#65a30d", "W"),
  /* Not a runbook family: the step where the engineer left the suggested
     path. Amber, and the only WALKED node drawn with a dashed outline — a
     heavy 2.2px dash, against the ghosts' hairline 1.3px one — so it still
     reads as the loudest thing on the canvas now that nothing is saturated. */
  _jump:          family("#f59e0b", "#f59e0b", "J"),
  /* Also not a runbook family: a question the engineer asked and the answer
     the agent gave, which has no nid and never will. Fuchsia — the one hue
     no runbook family uses — so a conversation turn is never mistaken for a
     node of the map. */
  _hybrid:        family("#d946ef", "#c026d3", "Q"),
  _default:       family("#6b7280", "#4b5563", "."),
};

export const typeStyle = (node) => {
  if (node?.kind === "jump") return TYPE_STYLE._jump;
  if (node?.kind === "hybrid") return TYPE_STYLE._hybrid;
  return TYPE_STYLE[node?.type] || TYPE_STYLE._default;
};

/**
 * The colours a node is actually DRAWN in.
 *
 * Same as typeStyle() for everything the walk touched — and NEUTRAL for a
 * branch it never took. A ghost used to be its family's hue at half tint,
 * which put an untaken violet PossibleCause in direct chromatic competition
 * with the violet the engineer DID walk; hue is the loudest channel on the
 * canvas and a path nobody took has no claim on it. Ghosts therefore borrow
 * `_default` — the same grey an unclassifiable node already falls back to —
 * and keep every OTHER ghost tell (dashed outline, NOT TAKEN chip, italic
 * label, the narrower SIB_SCALE box, no corner badges).
 *
 * A node the walk only PASSED THROUGH deliberately does NOT come here: it
 * was on the path, it is simply carrying no step, so it keeps its family
 * colour and is recessed by tint alone (see the node render below).
 */
export const nodePalette = (node) =>
  (node?.kind === "sibling" ? TYPE_STYLE._default : typeStyle(node));

/** The node fill as a CSS colour, for the HTML chips that cannot take #RRGGBBAA
    reliably through React's style object. */
export function tintOf(hex, alpha = 0.2) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex || ""));
  if (!m) return "rgba(148,163,184,0.2)";
  const n = parseInt(m[1], 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
}

/** Short human name per family, for the shape legend and the detail panel. */
export const TYPE_LABEL = {
  Problem: "Problem",
  Decision: "Decision",
  PossibleCause: "Possible cause",
  Symptom: "Symptom",
  Workflow: "Workflow start",
  Resolution: "Resolution",
  Remediation: "Remediation",
  Workaround: "Workaround",
  DiagnosticStep: "Check / step",
};

export function shapeForNode(node) {
  if (node.kind === "hybrid") return "stadium";
  if (node.kind === "jump") return "roundedRect";
  return SHAPE_FOR_TYPE[node.type] || "rect";
}

/* Geometry per shape: how much of the box the text may use, and how much
   taller than its text the box has to be for the text to fit INSIDE the
   outline. A diamond only offers ~half its width at the text band, which is
   why it is drawn 2.2x its content height — a 520x60 rhombus would clip
   every line it holds. Mirrors renderShape() in
   src/components/tse/TraversalGraph.jsx:28-80. */
function shapeMetrics(shape, W) {
  switch (shape) {
    case "diamond":
      return { textW: W * 0.5, hMul: 2.25, maxH: 240 };
    case "hexagon": {
      const i = Math.min(26, W * 0.12);
      return { textW: W - 2 * (i + 8), hMul: 1.12, maxH: 190 };
    }
    case "stadium":
      return { textW: W - 2 * 26, hMul: 1.05, maxH: 170 };
    case "roundedRect":
      return { textW: W - 2 * 16, hMul: 1, maxH: 170 };
    case "subroutine":
      return { textW: W - 2 * 20, hMul: 1, maxH: 170 };
    case "parallelogram":
    case "trapezoid": {
      const sk = Math.min(20, W * 0.07);
      return { textW: W - 2 * (sk + 12), hMul: 1.06, maxH: 175 };
    }
    default:
      return { textW: W - 2 * PAD_X, hMul: 1, maxH: MAX_H };
  }
}

/** The outline itself. Same shape language as the Diagnostic Map. */
function ShapeOutline({ shape, W, H, fill, stroke, strokeWidth, dash, opacity }) {
  const hw = W / 2;
  const hh = H / 2;
  const p = {
    fill, stroke, strokeWidth, strokeDasharray: dash || undefined, opacity,
  };
  switch (shape) {
    case "diamond":
      return <polygon points={`0,${-hh} ${hw},0 0,${hh} ${-hw},0`} {...p} />;
    case "hexagon": {
      const i = Math.min(26, hw * 0.24);
      return (
        <polygon
          points={`${-hw},0 ${-hw + i},${-hh} ${hw - i},${-hh} ${hw},0 ${hw - i},${hh} ${-hw + i},${hh}`}
          {...p}
        />
      );
    }
    case "stadium":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={hh} ry={hh} {...p} />;
    case "roundedRect":
      return <rect x={-hw} y={-hh} width={W} height={H} rx={14} ry={14} {...p} />;
    case "parallelogram": {
      const sk = Math.min(20, hw * 0.14);
      return <polygon points={`${-hw + sk},${-hh} ${hw},${-hh} ${hw - sk},${hh} ${-hw},${hh}`} {...p} />;
    }
    case "trapezoid": {
      const sk = Math.min(20, hw * 0.14);
      return <polygon points={`${-hw + sk},${-hh} ${hw - sk},${-hh} ${hw},${hh} ${-hw},${hh}`} {...p} />;
    }
    case "subroutine": {
      const ins = Math.min(12, hw * 0.1);
      return (
        <>
          <rect x={-hw} y={-hh} width={W} height={H} rx={4} ry={4} {...p} />
          <line x1={-hw + ins} y1={-hh} x2={-hw + ins} y2={hh} stroke={stroke} strokeWidth={1} opacity={opacity} />
          <line x1={hw - ins} y1={-hh} x2={hw - ins} y2={hh} stroke={stroke} strokeWidth={1} opacity={opacity} />
        </>
      );
    }
    default:
      return <rect x={-hw} y={-hh} width={W} height={H} rx={8} ry={8} {...p} />;
  }
}

/* ── view preference (shared by LearningMode and TrailReview) ────────── */

const VIEW_KEY = "arta.learning.trailView";
export const TRAIL_VIEWS = { LIST: "list", GRAPH: "graph" };

const readStoredView = () => {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    return v === TRAIL_VIEWS.GRAPH ? TRAIL_VIEWS.GRAPH : TRAIL_VIEWS.LIST;
  } catch {
    // Storage can throw in private mode / blocked-cookie contexts.
    return TRAIL_VIEWS.LIST;
  }
};

/** Remembers the last view the engineer chose, across screens and reloads. */
export function useTrailView() {
  const [view, setView] = useState(readStoredView);
  const choose = useCallback((next) => {
    setView(next);
    try {
      window.localStorage.setItem(VIEW_KEY, next);
    } catch { /* preference is a nicety — never break the screen over it */ }
  }, []);
  return [view, choose];
}

/** The List | Graph tabs that live in the section header. */
export function TrailViewToggle({ view, onChange, idPrefix = "tv" }) {
  const tabs = [
    { value: TRAIL_VIEWS.LIST, label: "List", Icon: List },
    { value: TRAIL_VIEWS.GRAPH, label: "Graph", Icon: Workflow },
  ];
  return (
    <div className="lg-view-tabs" role="tablist" aria-label="Trail view">
      {tabs.map(({ value, label, Icon }) => (
        <button
          key={value}
          id={`${idPrefix}-${value}`}
          type="button"
          role="tab"
          aria-selected={view === value}
          className={`lg-view-tab${view === value ? " lg-view-tab--active" : ""}`}
          onClick={() => onChange?.(value)}
        >
          <Icon size={12} />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
}

/* ── graph construction ─────────────────────────────────────────────── */

/* Node geometry.
   NODE_W is no longer a constant: a linear trail in a 660px pane used to be
   drawn as a 196px-wide column down the middle, which both wasted the width
   and forced fit-to-view to shrink the text to ~6px. The width is now derived
   from the measured canvas (nodeWidthFor below) so a chain fills the pane and
   fit-to-view lands at scale 1. */
const NODE_W_MIN = 196;
const NODE_W_MAX = 520;
/* Space Grotesk at 12px averages ~6.3px per character. Used to turn a node
   width into a character budget so wrapping tracks the real box. */
const CHAR_W = 6.3;
const LINE_H = 15;
const PAD_X = 14;
const MIN_H = 52;
const MAX_H = 150;
/* Height of the turn chip line, when one is drawn. */
const CHIP_H = 15;
const MAX_LINES = 3;

/* A branch that was never walked is context, not content: it is drawn
   narrower than the step that WAS taken so the eye reads the walked column
   as the spine. Also keeps a fork from halving the width of the real path. */
const SIB_SCALE = 0.62;
const SIB_W_MIN = 150;
/* Hard ceiling on ghost nodes. 12 siblings per step (META_MAX_SIBLINGS) on a
   40-step trail is 480 boxes — the graph would be unreadable and dagre slow. */
const MAX_SIBLING_NODES = 40;
const siblingWidth = (W) => Math.max(SIB_W_MIN, Math.round(W * SIB_SCALE));

/* ── how the pane is split between canvas and detail panel ───────────────
 *
 * The panel is where the engineer READS the node and MARKS it; the canvas is
 * where they find it. A fixed 220px sidebar could hold neither: the three
 * mark buttons wrapped onto two rows, the chip row spent a line per chip, and
 * a source card came out ~158px wide — enough for "1 Run…" and two clipped
 * lines of WHY SELECTED. DETAIL_MIN_W is the width at which all three of
 * those fit: 3 x ~86px of mark button, a one-row chip line, and a
 * single-column source card whose title and "why" both read.
 *
 * Above that the panel takes 40% of the pane and stops at DETAIL_MAX_W —
 * past ~460px it is only stretching 12.5px prose, and the canvas is the
 * better home for the pixels. (It was 30% / 300–420px: in the overlay's
 * ~900px left pane that left a 300px panel where the step text, the sources
 * card and the reason note were all cramped. 900px now gives 360px.)
 *
 * Below CANVAS_MIN_W + DETAIL_MIN_W there is no honest split left, so the
 * panel goes UNDER the canvas instead of beside it and both get the whole
 * pane width.
 *
 * Keyed off the PANE, never the viewport. The learning overlay's left pane is
 * half a wide viewport, so a `@media (max-width: …)` rule never fires there
 * however narrow the pane gets — the exact trap the old
 * `@media (max-width: 1200px)` + hand-written `.lm-left` pair was papering
 * over. paneW below comes from a ResizeObserver on .lg-root itself, so the
 * overlay, the /learning page and any future host all get the same rule.
 */
export const DETAIL_MIN_W = 340;
export const DETAIL_MAX_W = 460;
const CANVAS_MIN_W = 520;
/* Must match `gap` on .lg-root. */
const SPLIT_GAP = 10;

/**
 * The detail panel's width for a pane `paneW` px wide.
 *   null — not measured yet; the CSS default stands.
 *   0    — too narrow to split: stack the panel under the canvas.
 *   n    — the panel's width in px, canvas gets the rest.
 */
export function detailWidthFor(paneW) {
  const w = Number(paneW) || 0;
  if (w <= 0) return null;
  if (w - SPLIT_GAP - DETAIL_MIN_W < CANVAS_MIN_W) return 0;
  // Never take the canvas below CANVAS_MIN_W to reach 40%.
  const room = w - SPLIT_GAP - CANVAS_MIN_W;
  return Math.round(Math.min(DETAIL_MAX_W, room, Math.max(DETAIL_MIN_W, w * 0.4)));
}

/* Must match the dagre call in layoutGraph(). */
const NODESEP = 36;
const MARGIN_X = 24;
/* Breathing room frame() leaves around the laid-out graph. */
const FIT_SLACK = 18;

/**
 * The node width to use for a canvas `cw` px wide holding at most
 * `perRank` nodes side by side.
 *
 * A trail is usually a chain (perRank 1), and there the box should span the
 * pane: the old fixed 196px column left ~230px of dead space on either side
 * of a 700px canvas and forced fit-to-view to shrink the text to fit the
 * height. Widened to the share of the pane each column actually has, floored
 * so a wide fan-out stays drawable and capped so the text measure stays
 * readable.
 */
export function nodeWidthFor(cw, perRank = 1) {
  // `perRank` is a WEIGHTED column count — a ghost sibling only claims
  // SIB_SCALE of a column — so it can be fractional. The gutter count is the
  // real number of boxes, hence the ceil.
  const cols = Math.max(1, Number(perRank) || 1);
  if (!cw || cw < NODE_W_MIN) return NODE_W_MIN;
  const avail = cw - MARGIN_X * 2 - (Math.ceil(cols) - 1) * NODESEP;
  return Math.round(clampW(avail / cols));
}
const clampW = (v) => Math.min(NODE_W_MAX, Math.max(NODE_W_MIN, v));

/**
 * How many nodes share the widest rank of an already-laid-out graph.
 *
 * Read off the layout rather than the edge list: a trail with a single
 * engineer_jump branch is still one column deep almost everywhere, and a
 * boolean "is it linear" test threw that whole trail back to the narrow box.
 * Counted as the maximum number of vertically overlapping boxes, WEIGHTED:
 * a ghost sibling is drawn at SIB_SCALE, so a fork of one taken step plus one
 * untaken branch costs 1.62 columns, not 2. Without the weight, adding
 * siblings would have thrown every forked trail back to the narrow box.
 */
export function maxNodesPerRank(nodes) {
  if (!nodes || nodes.length <= 1) return 1;
  const spans = nodes
    .map((n) => [n.y - n.H / 2, n.y + n.H / 2, n.kind === "sibling" ? SIB_SCALE : 1])
    .sort((a, b) => a[0] - b[0]);
  let best = 1;
  for (let i = 0; i < spans.length; i++) {
    let count = spans[i][2];
    for (let j = i + 1; j < spans.length && spans[j][0] < spans[i][1]; j++) count += spans[j][2];
    if (count > best) best = count;
  }
  return best;
}

/** Word-wrap into at most `maxLines` lines of `maxChars`, ellipsising the last. */
function wrapLabel(text, maxChars = 26, maxLines = MAX_LINES) {
  const words = String(text || "").trim().split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let cur = "";
  for (const w of words) {
    if (!cur) { cur = w; continue; }
    if ((cur + " " + w).length <= maxChars) cur += " " + w;
    else { lines.push(cur); cur = w; }
  }
  if (cur) lines.push(cur);
  if (lines.length > maxLines) {
    const kept = lines.slice(0, maxLines);
    kept[maxLines - 1] = `${kept[maxLines - 1].slice(0, maxChars - 1)}…`;
    return kept;
  }
  return lines.map((l) => (l.length > maxChars ? `${l.slice(0, maxChars - 1)}…` : l));
}

const sortedSteps = (steps) =>
  (Array.isArray(steps) ? steps : [])
    .filter(Boolean)
    .slice()
    .sort((a, b) => (Number(a.step_no) || 0) - (Number(b.step_no) || 0));

/**
 * The node a step belongs to.
 *   engineer_jump -> its OWN synthetic node. `to_nid` is null on a jump (the
 *                    engineer left the suggested path), so there is nothing to
 *                    point an edge at — we never draw an edge to "null", we
 *                    draw the jump itself as a distinct node.
 *   normal step    -> the node it landed on (`to_nid`), else the one it left.
 *   no nids at all -> a synthetic node, so every step stays markable here too.
 */
function anchorOf(step) {
  if (step.type === "digression") return `jump:${step.step_no}`;
  if (step.to_nid) return String(step.to_nid);
  if (step.from_nid) return String(step.from_nid);
  return `step:${step.step_no}`;
}

/**
 * Turn the trail's steps into { nodes, edges }.
 *
 * Edges, in order of trust:
 *   "walk"  — a real from_nid -> to_nid hop recorded on a step
 *   "jump"  — from_nid -> the synthetic jump node (dashed, amber)
 *   "flow"  — consecutive steps whose nids do not touch; dotted, unlabelled,
 *             drawn only so a trail with gaps still reads as one sequence
 */
/* A stable identity for "no conversation turns". A fresh [] default would
   rebuild the graph model on every render, and the auto-fit effect that
   follows a model change would then setView() forever. */
const NO_HYBRID = Object.freeze([]);

/** A step's turn as a number, or null — the same coercion groupStepsByTurn uses. */
function turnOf(step) {
  const raw = step?.turn_no;
  return raw == null || raw === "" || Number.isNaN(Number(raw)) ? null : Number(raw);
}

/**
 * The walk and the conversation in ONE sequence.
 *
 * Steps keep their own order (step_no) — sorting the whole lot by turn would
 * reorder a walk whose turn numbers disagree with its step numbers. Each
 * hybrid turn is INSERTED at its turn position instead: after the last step
 * of its own turn (the card shows the walk first, then the Q&A, and the two
 * views must agree), before the first step of a later turn. Turns with no
 * steps still land in the right place, and a hybrid turn later than every
 * step lands at the end.
 */
function sequenceUnits(orderedSteps, hybrid) {
  const units = orderedSteps.map((step) => ({ kind: "step", step }));
  const rows = (Array.isArray(hybrid) ? hybrid : [])
    .filter((h) => h && typeof h === "object" && Number.isFinite(Number(h.turn_no)))
    .slice()
    .sort((a, b) => Number(a.turn_no) - Number(b.turn_no));
  rows.forEach((h) => {
    const turn = Number(h.turn_no);
    let at = units.findIndex(
      (u) => u.kind === "step" && turnOf(u.step) != null && turnOf(u.step) > turn
    );
    if (at < 0) at = units.length;
    units.splice(at, 0, { kind: "hybrid", hybrid: h, turn });
  });
  return units;
}

export function buildTrailGraph(steps, nodeW = NODE_W_MIN, hybrid = NO_HYBRID) {
  const W = Math.max(NODE_W_MIN, Math.round(nodeW));
  const ordered = sortedSteps(steps);
  const nodes = new Map();
  const edges = [];
  const edgeSeen = new Set();

  const ensure = (id, seed = {}) => {
    let n = nodes.get(id);
    if (!n) {
      n = {
        id,
        nid: seed.nid ?? null,
        kind: seed.kind || "node",
        label: seed.label || "",
        steps: [],
        turns: [],
        order: nodes.size,
      };
      nodes.set(id, n);
    }
    if (seed.kind && n.kind === "node") n.kind = seed.kind;
    if (seed.nid && !n.nid) n.nid = seed.nid;
    return n;
  };

  const addEdge = (from, to, type, label) => {
    if (!from || !to || from === to) return;
    const key = `${from}\u0000${to}`;
    if (edgeSeen.has(key)) return;
    edgeSeen.add(key);
    edges.push({ from, to, type, label: label || "" });
  };

  let prevAnchor = null;
  sequenceUnits(ordered, hybrid).forEach((unit) => {
    /* A conversation turn: no nid, no step, no walk edge — it hangs in the
       sequence on flow edges alone, which is exactly what it is. */
    if (unit.kind === "hybrid") {
      const hid = `hyb:${unit.turn}`;
      const hnode = ensure(hid, { kind: "hybrid", label: unit.hybrid.question_text || "" });
      hnode.hybrid = unit.hybrid;
      if (!hnode.turns.includes(unit.turn)) hnode.turns.push(unit.turn);
      if (prevAnchor) addEdge(prevAnchor, hid, "flow");
      prevAnchor = hid;
      return;
    }
    const step = unit.step;
    const id = anchorOf(step);
    const isJump = step.type === "digression";
    const node = ensure(id, {
      nid: isJump ? null : (step.to_nid || step.from_nid || null),
      kind: isJump ? "jump" : (step.to_nid || step.from_nid ? "node" : "orphan"),
    });
    node.steps.push(step);
    const tn = step.turn_no;
    const turn = tn == null || tn === "" || Number.isNaN(Number(tn)) ? null : Number(tn);
    if (turn != null && !node.turns.includes(turn)) node.turns.push(turn);

    if (step.from_nid) {
      ensure(String(step.from_nid), { nid: String(step.from_nid) });
      addEdge(String(step.from_nid), id, isJump ? "jump" : "walk", isJump ? "" : step.edge_label);
    }
    if (prevAnchor) addEdge(prevAnchor, id, "flow");
    prevAnchor = id;
  });

  /* ── the branches NOT taken ───────────────────────────────────────────
     A ghost is an EDGE that was not crossed, not a node: `siblings` lists the
     other destinations of the SAME fork, and `meta.sibling_labels` gives that
     fork's condition for each. So the node is keyed by (fork, branch), NOT by
     the bare nid.

     Keying by nid alone was wrong twice over, and both showed up on the first
     real trail: two forks whose runbook branches converge on the same node
     collapsed into ONE ghost — the second fork's branch vanished from the
     graph even though the list view showed it — and that single ghost then
     took an inbound dashed edge from each fork, reading as a detached node
     with arrows converging into it instead of a branch hanging off its
     parent. One ghost per fork per branch: exactly one inbound edge each,
     each carrying its own condition text.

     Run AFTER the walk so the only thing that can suppress a ghost is the
     walk having actually crossed THAT edge (the engineer backtracked and took
     the other branch later). Compared case-insensitively: the ids the logger
     records and the ones the mermaid parser emits agree on the letters and
     not always on the case — capture.py:_adjacency says the same.

     A ghost carries no step, so it has no step_no, no result and nothing to
     mark — it can never reach `results` or the submit gate. */
  const walkedEdges = new Set(
    edges
      .filter((e) => e.type === "walk")
      .map((e) => `${e.from.toLowerCase()} ${e.to.toLowerCase()}`)
  );
  let ghosts = 0;
  ordered.forEach((step) => {
    if (!step.from_nid) return;
    const parent = String(step.from_nid);
    stepEvidence(step).siblings.forEach((sib) => {
      const nid = sib.nid;
      if (!nid) return;
      if (walkedEdges.has(`${parent.toLowerCase()} ${nid.toLowerCase()}`)) return;
      const id = `sib:${parent}:${nid}`;
      if (!nodes.has(id)) {
        if (ghosts >= MAX_SIBLING_NODES) return;
        ghosts += 1;
      }
      ensure(id, { nid, kind: "sibling", label: sib.label });
      addEdge(parent, id, "untaken", "");
    });
  });

  // Label + box size per node. The character budget follows the real box
  // width AND the shape: a diamond only offers half its width to text, so
  // its budget is half too rather than wrapping into the outline.
  const list = [...nodes.values()].map((n) => {
    const first = n.steps[0];
    const isSib = n.kind === "sibling";
    const isHybrid = n.kind === "hybrid";
    const text = isSib
      ? (n.label || n.nid || n.id)
      : isHybrid
        ? (n.label || n.hybrid?.answer_text || "Question")
        : (first ? stepHeadline(first) : (n.nid || n.id));
    /* A conversation turn has no nid to classify, and classifying it by its
       TEXT would call every question a Decision. It is its own kind. */
    const type = isHybrid ? null : nodeTypeFromNid(n.nid, isSib ? n.label : text);
    const shape = shapeForNode({ ...n, type });
    const boxW = isSib ? siblingWidth(W) : W;
    const { textW, hMul, maxH } = shapeMetrics(shape, boxW);
    const maxChars = Math.max(14, Math.floor(textW / CHAR_W));
    const lines = wrapLabel(text, maxChars, MAX_LINES);
    /* The chip line is the turn badge on a walked node and the NOT TAKEN
       badge on a ghost — either way it is a line the box has to hold. */
    const chip = n.turns.length || isSib ? CHIP_H : 0;
    const content = chip + lines.length * LINE_H + 18;
    const H = Math.round(Math.min(maxH, Math.max(MIN_H, content * hMul)));
    return { ...n, type, shape, lines, textW, chipH: chip, W: boxW, H };
  });

  return { nodes: list, edges };
}

/* ── layout ─────────────────────────────────────────────────────────── */

/** A single column, in walk order — used when dagre is unavailable. */
function stackLayout(nodes) {
  let y = 24;
  return nodes.map((n) => {
    const placed = { ...n, x: 24 + n.W / 2, y: y + n.H / 2 };
    y += n.H + 44;
    return placed;
  });
}

function layoutGraph(model) {
  const { nodes, edges } = model;
  if (!nodes.length) return { nodes: [], edges: [], width: 0, height: 0 };

  let g = null;
  try {
    g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: "TB", nodesep: NODESEP, ranksep: 52, marginx: MARGIN_X, marginy: 24 });
    g.setDefaultEdgeLabel(() => ({}));
    nodes.forEach((n) => g.setNode(n.id, { width: n.W, height: n.H }));
    edges.forEach((e) => g.setEdge(e.from, e.to, {}));
    dagre.layout(g);
  } catch {
    // dagre needs structuredClone; rather than take the whole trail screen
    // down with it, fall back to a plain column and straight connectors.
    g = null;
  }

  const placed = g
    ? nodes.map((n, i) => {
      const pos = g.node(n.id);
      // dagre positions every node it was given, but never trust it blindly —
      // a fallback column keeps a node on canvas instead of at NaN.
      const x = Number.isFinite(pos?.x) ? pos.x : 40 + i * (n.W + 36);
      const y = Number.isFinite(pos?.y) ? pos.y : 40 + i * 90;
      return { ...n, x, y };
    })
    : stackLayout(nodes);
  const byId = Object.fromEntries(placed.map((n) => [n.id, n]));
  const routed = edges.map((e) => {
    const de = g ? g.edge(e.from, e.to) : null;
    return { ...e, points: de?.points || null, a: byId[e.from], b: byId[e.to] };
  }).filter((e) => e.a && e.b);

  const width = Math.max(...placed.map((n) => n.x + n.W / 2)) + 24;
  const height = Math.max(...placed.map((n) => n.y + n.H / 2)) + 24;
  return { nodes: placed, edges: routed, width, height };
}

/* ── the two channels ───────────────────────────────────────────────────
 *
 * COLOUR + SHAPE say what a node IS (TYPE_STYLE / SHAPE_FOR_TYPE above) —
 * the Diagnostic Map's language, unchanged, so the same node looks the same
 * in both views.
 *
 * The engineer's MARK therefore cannot be the fill any more. It moved to a
 * corner BADGE: a small disc on the node's top-left carrying ✓ / ✗ / ? / ±,
 * mirroring the numbered badge the Diagnostic Map already hangs on a visited
 * node (TraversalGraph.jsx:876-883) — and that numbered badge is kept, on the
 * top-RIGHT, so the two never sit on top of each other. A badge was chosen
 * over a ring because the ring channel is already spoken for twice over:
 * cyan ring = the turn the transcript is on, amber ring = selection, both
 * lifted from the Diagnostic Map. The glyph also survives greyscale and a
 * 0.62 zoom-out, which a hue never does.
 *
 * `fill` here is the BADGE colour, not the node colour.
 */
const MARK_STYLE = {
  worked:        { fill: "#059669", border: "#047857", glyph: "✓" },
  ruled_out:     { fill: "#be123c", border: "#9f1239", glyph: "✗" },
  inconclusive:  { fill: "#6b7280", border: "#4b5563", glyph: "?" },
  mixed:         { fill: "#334155", border: "#1e293b", glyph: "±" },
  /* Drawn hollow: an empty slot reads as work still to do, which is exactly
     what the submit gate is counting. */
  unmarked:      { fill: "#ffffff", border: "#94a3b8", glyph: "" },
  /* No step behind the node — nothing to mark, so no badge at all. */
  passthrough:   { fill: "#f8fafc", border: "#e2e5eb", glyph: "" },
  untaken:       { fill: "#f8fafc", border: "#b6c2d1", glyph: "" },
};

/** The mark, in words — for the chip the detail panel pins at the top. */
const MARK_LABEL = {
  worked: "Worked",
  ruled_out: "Ruled out",
  inconclusive: "Can't tell",
  mixed: "Mixed",
  unmarked: "Not marked",
};

/** Where the two corner badges hang, per shape — on the outline, not in the
    empty space a diamond or a stadium leaves around its bounding box. */
function badgeSpots(shape, W, H) {
  const hw = W / 2;
  const hh = H / 2;
  switch (shape) {
    case "diamond":
      // Midpoints of the two upper edges.
      return { left: [-hw / 2, -hh / 2], right: [hw / 2, -hh / 2] };
    case "hexagon": {
      const i = Math.min(26, hw * 0.24);
      return { left: [-hw + i, -hh], right: [hw - i, -hh] };
    }
    case "stadium":
      // Where the rounded end gives way to the straight top edge, so the
      // badge sits ON the outline instead of floating off the curve.
      return { left: [-hw + hh, -hh + 4], right: [hw - hh, -hh + 4] };
    case "parallelogram":
    case "trapezoid": {
      const sk = Math.min(20, hw * 0.14);
      return { left: [-hw + sk, -hh], right: [hw - sk / 2, -hh] };
    }
    default:
      return { left: [-hw + 2, -hh + 2], right: [hw - 2, -hh + 2] };
  }
}

/* navigator.clipboard is undefined on non-secure origins (plain-http
   staging) and writeText rejects when the tab is not focused — same legacy
   fallback DiagnosticMapWindow.jsx:39-58 and TraversalGraph.jsx:9-26 carry.
   Duplicated for the same reason they duplicate it from each other: neither
   module exports it. */
async function copyTextToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through to the legacy path */ }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "-1000px";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
}

/** The mark that colours a node: unmarked wins, then unanimous, else mixed. */
function nodeMark(node, results) {
  if (node.kind === "sibling") return "untaken";
  if (node.kind === "hybrid") return "hybrid";
  if (!node.steps.length) return "passthrough";
  const marks = node.steps.map((s) => results[s.step_no] || null);
  if (marks.some((m) => !m)) return "unmarked";
  return marks.every((m) => m === marks[0]) ? marks[0] : "mixed";
}

/* ── the canvas ─────────────────────────────────────────────────────── */

const MIN_SCALE = 0.25;
const MAX_SCALE = 2.5;
/* Fit-to-view will not shrink below this: 12px label text x 0.62 is still
   ~7.5px, which is the smallest the node text stays legible at. */
const READABLE_SCALE = 0.62;
const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

const EDGE_STROKE = {
  walk: "#94a3b8", jump: "#f59e0b", flow: "#dbe1e8", untaken: "#c2ccd8",
};
const EDGE_DASH = {
  jump: "5 4", flow: "2 5", untaken: "4 4",
};

function edgePath(e) {
  const pts = e.points && e.points.length >= 2 ? e.points : null;
  if (!pts) {
    return `M${e.a.x},${e.a.y + e.a.H / 2} L${e.b.x},${e.b.y - e.b.H / 2}`;
  }
  return pts.reduce(
    (d, p, i) => (i === 0 ? `M${p.x},${p.y}` : `${d} L${p.x},${p.y}`),
    ""
  );
}

/** Midpoint of an edge, for the condition pill. */
function edgeMid(e) {
  const pts = e.points && e.points.length >= 2 ? e.points : null;
  if (!pts) {
    return { x: (e.a.x + e.b.x) / 2, y: (e.a.y + e.a.H / 2 + e.b.y - e.b.H / 2) / 2 };
  }
  const m = pts[Math.floor(pts.length / 2)];
  return { x: m.x, y: m.y };
}

/* The condition an edge carries, in the same small bordered pill the
   Diagnostic Map draws it in (TraversalGraph.jsx:712-725): 9px semibold on
   near-white, 1px slate border, clipped at 28 characters. */
const EDGE_LABEL_MAX = 28;
function EdgeLabel({ edge }) {
  const raw = String(edge.label || "").trim();
  if (!raw) return null;
  const lbl = raw.length > EDGE_LABEL_MAX ? `${raw.slice(0, EDGE_LABEL_MAX - 1)}…` : raw;
  const { x, y } = edgeMid(edge);
  const w = lbl.length * 5.2 + 12;
  return (
    <g className="lg-edge-label" pointerEvents="none">
      <rect x={x - w / 2} y={y - 8} width={w} height={16} rx={3}
        fill="rgba(255,255,255,0.95)" stroke="#cbd5e1" strokeWidth={1} />
      <text x={x} y={y} textAnchor="middle" dominantBaseline="central">{lbl}</text>
    </g>
  );
}

/** A node's neighbours, as the Diagnostic Map's inspector lists them:
    Incoming / Outgoing, each row a jump-to button tagged with whether the
    walk actually crossed that edge. Only real hops are listed — the dotted
    "flow" connectors exist purely so a trail with gaps reads as one
    sequence and would be noise here. */
function NodeEdges({ node, edges, byId, onPick }) {
  const real = edges.filter((e) => e.type !== "flow");
  const inc = real.filter((e) => e.to === node.id);
  const out = real.filter((e) => e.from === node.id);
  if (!inc.length && !out.length) return null;
  const nameOf = (id) => {
    const n = byId[id];
    if (!n) return id;
    if (n.kind === "jump") return "Your own way";
    return n.nid || n.lines?.join(" ") || n.id;
  };
  const row = (e, id) => {
    const n = byId[id];
    const crossed = e.type !== "untaken";
    return (
      <button
        key={`${e.from}->${e.to}`}
        type="button"
        className="lg-detail-edge"
        onClick={() => onPick(id)}
      >
        <i style={{ background: n ? nodePalette(n).border : "#cbd5e1" }} />
        <span className="lg-detail-edge-name">{nameOf(id)}</span>
        {e.label && <span className="lg-detail-edge-cond">{e.label}</span>}
        <span className={`lg-detail-edge-tag${crossed ? "" : " lg-detail-edge-tag--not"}`}>
          {crossed ? "TRAVERSED" : "NOT TAKEN"}
        </span>
      </button>
    );
  };
  return (
    <div className="lg-detail-edges">
      {inc.length > 0 && (
        <div>
          <p className="lg-detail-edges-label">Incoming ({inc.length})</p>
          {inc.map((e) => row(e, e.from))}
        </div>
      )}
      {out.length > 0 && (
        <div>
          <p className="lg-detail-edges-label">Outgoing ({out.length})</p>
          {out.map((e) => row(e, e.to))}
        </div>
      )}
    </div>
  );
}

/**
 * Props mirror StepList so the two views stay interchangeable:
 *   steps, results, readOnly, activeTurn, onMark(stepNo, value), onStepClick(turnNo)
 *
 *   notes  — { [step_no]: text }, the live "why" behind a Ruled out mark, and
 *   onNote — (stepNo, value) => void
 * Both OPTIONAL and both shared with the list: a note typed in the detail panel
 * is already there when the reviewer flips back to List, because it is the same
 * map and the same handler, exactly as `results` / `onMark` are.
 *
 *   hybrid        — the fast Q&A rows, drawn as their own node kind at their
 *                   turn position so the graph shows the whole conversation
 *                   and not only the runbook walk.
 *   hybridMarks / hybridReasons — { [turn_no]: … }, and
 *   onMarkHybrid / onReasonHybrid — (turnNo, value) => void.
 * The two vocabularies stay apart here exactly as they do in the list: a step
 * node marks Worked / Ruled out / Can't tell through `onMark`, a conversation
 * node marks Helpful / Partly / Wrong through `onMarkHybrid`, and they persist
 * through their own endpoints.
 */
export default function TrailGraph({
  steps = [],
  results = {},
  notes = {},
  hybrid = NO_HYBRID,
  hybridMarks = {},
  hybridReasons = {},
  confirmedMarks = {},
  confirmedHybrid = {},
  readOnly = false,
  activeTurn = null,
  onMark,
  onNote,
  onMarkHybrid,
  onReasonHybrid,
  onStepClick,
}) {
  const wrapRef = useRef(null);
  const rootRef = useRef(null);
  // Measured canvas width. 0 until the first layout pass; the model is built
  // against NODE_W_MIN then and rebuilt once the real width is known.
  const [canvasW, setCanvasW] = useState(0);
  /* Measured width of the WHOLE graph block — the pane the component was
     handed, not the viewport. Drives the canvas/panel split below. */
  const [paneW, setPaneW] = useState(0);

  /* Two passes, and the first is not wasted: the box width depends on how
     many nodes dagre puts side by side, and that is only knowable from a
     layout. Pass 1 lays the trail out at the minimum width purely to count
     the widest rank; pass 2 is the one that gets drawn. Rank membership is a
     function of the topology, not of box width, so the count is stable and
     this cannot oscillate. */
  const shape = useMemo(() => buildTrailGraph(steps, NODE_W_MIN, hybrid), [steps, hybrid]);
  const probe = useMemo(() => layoutGraph(shape), [shape]);
  const perRank = useMemo(() => maxNodesPerRank(probe.nodes), [probe]);
  /* The widest RANK is not the widest the graph gets: dagre spreads columns
     globally, so a trail whose forks hang off different ranks is wider than
     any one of them. Sizing off the rank alone left a 4-branch trail laid out
     at 1029px in an 870px pane, i.e. fit-to-width at 0.83 and 9.9px labels.
     Convert the probe's REAL horizontal extent into an equivalent number of
     minimum-width columns and size against that instead; never below the
     rank count, which is the floor the boxes physically need. */
  const columns = useMemo(() => Math.max(
    perRank,
    (probe.width - MARGIN_X * 2 + NODESEP) / (NODE_W_MIN + NODESEP),
  ), [probe, perRank]);
  /* Size the boxes against the pane MINUS the slack frame() keeps around the
     graph (laid.width + 16) — otherwise a rank sized to exactly the pane
     comes out one pixel too wide and fit-to-width shrinks the whole trail to
     0.98, i.e. 11.8px text, for nothing. */
  const nodeW = useMemo(
    () => nodeWidthFor(canvasW ? canvasW - FIT_SLACK : 0, columns),
    [canvasW, columns]
  );
  const model = useMemo(
    () => (nodeW === NODE_W_MIN ? shape : buildTrailGraph(steps, nodeW, hybrid)),
    [shape, steps, hybrid, nodeW]
  );
  const laid = useMemo(() => (nodeW === NODE_W_MIN ? probe : layoutGraph(model)), [probe, model, nodeW]);

  const [view, setView] = useState({ x: 0, y: 0, k: 1 });
  const [selectedId, setSelectedId] = useState(null);
  const panRef = useRef(null);
  const movedRef = useRef(false);
  // Once the engineer pans or zooms, the framing is theirs — auto-fit stops.
  const userFramedRef = useRef(false);

  const selected = useMemo(
    () => laid.nodes.find((n) => n.id === selectedId) || null,
    [laid.nodes, selectedId]
  );
  const nodeById = useMemo(
    () => Object.fromEntries(laid.nodes.map((n) => [n.id, n])),
    [laid.nodes]
  );
  /* The selected node's mark, for the chip pinned at the top of the detail
     panel — the same nodeMark() the corner badge is drawn from, so the two
     can never disagree. Null when there is nothing to mark: a ghost, or a
     node the walk only passed through. */
  const selMark = useMemo(() => (
    !selected || selected.kind === "sibling" || selected.kind === "hybrid" || !selected.steps.length
      ? null
      : nodeMark(selected, results)
  ), [selected, results]);

  /* Type legend — only the families this trail contains, in the order they
     are drawn in. A jump has no family and is already in the state legend,
     so it is skipped. */
  const typesUsed = useMemo(() => {
    const seen = [];
    laid.nodes.forEach((n) => {
      if (n.kind === "jump" || n.kind === "hybrid" || !n.type) return;
      if (!seen.includes(n.type)) seen.push(n.type);
    });
    return seen;
  }, [laid.nodes]);

  /* The walk, in order — the nodes that actually carry a step, earliest
     step_no first. Drives both the numbered badge on a node and the Trace
     Path stepper. A ghost and a passed-through node carry no step, so
     neither is numbered and neither can be stepped onto. */
  /* Does this trail carry any conversation turn at all? Drives the extra
     legend row — a guided-only trail must not be told about a node kind it
     does not contain. */
  const hasHybridNodes = useMemo(
    () => laid.nodes.some((n) => n.kind === "hybrid"),
    [laid.nodes]
  );

  const walk = useMemo(() => (
    laid.nodes
      .filter((n) => n.steps.length)
      .map((n) => ({ n, first: Math.min(...n.steps.map((s) => Number(s.step_no) || 0)) }))
      .sort((a, b) => a.first - b.first)
      .map(({ n }) => n)
  ), [laid.nodes]);
  const walkNo = useMemo(
    () => Object.fromEntries(walk.map((n, i) => [n.id, i + 1])),
    [walk]
  );

  /* Frame the trail in the pane.
     Default (`whole` false) fits the WIDTH only and lets a long trail run off
     the bottom to be panned. Fitting both axes is what made this view
     unusable: a 6-step chain in a 420px canvas came out at scale 0.49, i.e.
     ~5px label text, with the column stranded in the middle of an otherwise
     empty pane. Width-fit keeps the labels at their real size and puts the
     boxes across the pane, which is the axis a mostly-linear trail has spare.
     The toolbar's Fit button passes `whole` for a bird's-eye of the trail,
     still floored at READABLE_SCALE. */
  const frame = useCallback((whole) => {
    const el = wrapRef.current;
    if (!el || !laid.nodes.length) return;
    const cw = el.clientWidth || 1;
    const ch = el.clientHeight || 1;
    const byWidth = cw / (laid.width + 16);
    const raw = whole ? Math.min(byWidth, ch / (laid.height + 16)) : byWidth;
    const k = clamp(Math.max(raw, whole ? READABLE_SCALE : MIN_SCALE), MIN_SCALE, 1);
    const h = laid.height * k;
    setView({
      x: (cw - laid.width * k) / 2,
      // Top-align when the trail is taller than the canvas, so it starts at
      // step 1 rather than halfway down.
      y: h > ch ? 8 : (ch - h) / 2,
      k,
    });
  }, [laid]);

  const fitView = useCallback(() => frame(false), [frame]);
  const fitWhole = useCallback(() => { userFramedRef.current = false; frame(true); }, [frame]);

  /* Auto-fit on mount, on tab switch (this component remounts), whenever the
     laid-out trail changes, and on resize — but only until the engineer takes
     over the framing themselves. */
  useLayoutEffect(() => {
    if (!userFramedRef.current) fitView();
  }, [fitView]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    // Seed the measurement synchronously so the first paint after mount
    // already has the real width (ResizeObserver only fires on the next frame).
    const seed = el.clientWidth;
    if (seed) setCanvasW((w) => (Math.abs(w - seed) > 2 ? seed : w));
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      const w = el.clientWidth;
      // Ignore sub-pixel churn — every change here rebuilds the whole model.
      setCanvasW((prev) => (Math.abs(prev - w) > 2 ? w : prev));
      if (!userFramedRef.current) fitView();
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fitView]);

  /* The pane's own width, measured — see detailWidthFor(). useLayoutEffect
     so the first paint already carries the right split instead of flashing
     the CSS default. .lg-root's width is set by its PARENT (min-width:0,
     max-width:100%), never by its own grid tracks, so writing the tracks back
     from this measurement cannot feed back into it. */
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const read = () => setPaneW((prev) => {
      const w = el.clientWidth;
      // Ignore sub-pixel churn; a change here re-splits the whole block.
      return Math.abs(prev - w) > 2 ? w : prev;
    });
    read();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* null while unmeasured (the CSS default stands), 0 = stack under the
     canvas, otherwise the panel's width in px. */
  const detailW = detailWidthFor(paneW);
  const stacked = detailW === 0;

  // Drop a selection that no longer exists (step deleted, trail refreshed).
  useEffect(() => {
    if (selectedId && !laid.nodes.some((n) => n.id === selectedId)) setSelectedId(null);
  }, [laid.nodes, selectedId]);

  /* ── pan / zoom ── */
  const onWheel = useCallback((e) => {
    // The canvas owns the wheel here; letting it bubble would scroll the whole
    // left pane out from under the graph.
    e.preventDefault();
    e.stopPropagation();
    const el = wrapRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;
    userFramedRef.current = true;
    setView((v) => {
      const k = clamp(v.k * (e.deltaY < 0 ? 1.12 : 1 / 1.12), MIN_SCALE, MAX_SCALE);
      const ratio = k / v.k;
      return { k, x: px - (px - v.x) * ratio, y: py - (py - v.y) * ratio };
    });
  }, []);

  // React attaches wheel passively on the root; a non-passive native listener
  // is the only way preventDefault actually holds.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [onWheel]);

  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    movedRef.current = false;
    panRef.current = { px: e.clientX, py: e.clientY, x: view.x, y: view.y };
    /* Pointer capture is NOT taken here — it is deferred to onPointerMove so
       a pure click (no movement) lets the click event fire on the SVG <g>
       node underneath. Taking capture here redirected pointerup to the canvas
       wrapper, which prevented the click from reaching any child element and
       broke node selection entirely. */
  };
  const onPointerMove = (e) => {
    const p = panRef.current;
    if (!p) return;
    const dx = e.clientX - p.px;
    const dy = e.clientY - p.py;
    if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
      if (!movedRef.current) {
        movedRef.current = true;
        userFramedRef.current = true;
        /* Capture now that a real pan has started — this keeps the drag smooth
           even if the pointer leaves the canvas, just as before. */
        e.currentTarget.setPointerCapture?.(e.pointerId);
      }
    }
    if (movedRef.current) {
      setView((v) => ({ ...v, x: p.x + dx, y: p.y + dy }));
    }
  };
  const endPan = (e) => {
    panRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
  };

  const zoomBy = (f) => {
    userFramedRef.current = true;
    return setView((v) => {
      const el = wrapRef.current;
      const cw = (el?.clientWidth || 0) / 2;
      const ch = (el?.clientHeight || 0) / 2;
      const k = clamp(v.k * f, MIN_SCALE, MAX_SCALE);
      const ratio = k / v.k;
      return { k, x: cw - (cw - v.x) * ratio, y: ch - (ch - v.y) * ratio };
    });
  };

  /* Selecting a node behaves exactly like clicking a step card: it drives
     `activeTurn`, so the transcript on the right follows. */
  const selectNode = useCallback((node) => {
    if (movedRef.current) return;   // a pan must not select
    setSelectedId(node.id);
    const turn = node.turns.length ? node.turns[0] : node.steps[0]?.turn_no;
    if (turn != null) onStepClick?.(turn);
  }, [onStepClick]);

  /* Trace Path: walk the recorded steps one node at a time, the way the
     Diagnostic Map's step navigator does. Selecting through here goes via
     the same path a click does, so the transcript follows too. */
  const centerOn = useCallback((node) => {
    const el = wrapRef.current;
    if (!el || !node) return;
    const cw = el.clientWidth || 1;
    const ch = el.clientHeight || 1;
    userFramedRef.current = true;
    setView((v) => ({ ...v, x: cw / 2 - node.x * v.k, y: ch / 2 - node.y * v.k }));
  }, []);

  const traceStep = walk.findIndex((n) => n.id === selectedId) + 1;
  const goToStep = useCallback((i) => {
    const node = walk[i - 1];
    if (!node) return;
    setSelectedId(node.id);
    const turn = node.turns.length ? node.turns[0] : node.steps[0]?.turn_no;
    if (turn != null) onStepClick?.(turn);
    centerOn(node);
  }, [walk, onStepClick, centerOn]);

  const [copiedId, setCopiedId] = useState(null);
  const copyNid = useCallback(async (text) => {
    if (!text) return;
    if (await copyTextToClipboard(text)) {
      setCopiedId(text);
      setTimeout(() => setCopiedId((c) => (c === text ? null : c)), 1200);
    }
  }, []);

  /* Empty state: only when there are no steps AND no hybrid turns to draw.
     An informational session has only hybrid turns (no diagnostic steps), so
     checking steps alone hid the whole graph while the list view showed all
     four turns — the bug on trail 3436. */
  if (!steps.length && (!hybrid || !hybrid.length)) {
    return <p className="tr-empty-hint">No steps captured yet.</p>;
  }

  return (
    <div
      className="lg-root"
      ref={rootRef}
      data-layout={stacked ? "stack" : "split"}
      style={detailW ? { "--lg-detail-w": `${detailW}px` } : undefined}
    >
      <div className="lg-canvas-col">
        {/* Channel 1 — what the node IS. Colour AND shape together, one row
            per family this trail actually contains. */}
        {typesUsed.length > 0 && (
          <div className="lg-legend lg-legend--types">
            {typesUsed.map((t) => {
              const st = TYPE_STYLE[t] || TYPE_STYLE._default;
              return (
                <span key={t} className="lg-legend-item">
                  <svg width="18" height="14" viewBox="-9 -7 18 14" aria-hidden="true">
                    <ShapeOutline
                      shape={SHAPE_FOR_TYPE[t] || "rect"}
                      W={16} H={12}
                      fill={`${st.bg}${NODE_FILL_A}`} stroke={st.border} strokeWidth={1.2}
                    />
                  </svg>
                  <span style={{ color: st.text }}>{TYPE_LABEL[t] || t}</span>
                </span>
              );
            })}
            <span className="lg-legend-item lg-legend-item--jump">
              <i /> You went your own way
            </span>
            {hasHybridNodes && (
              <span className="lg-legend-item lg-legend-item--hybrid">
                <i /> Question &amp; answer — no runbook node
              </span>
            )}
            {/* Solid vs dashed is the channel that now separates the walk from
                the branches it skipped — spelled out, side by side. */}
            <span className="lg-legend-item lg-legend-item--walked">
              <i /> Walked — solid outline
            </span>
            <span className="lg-legend-item lg-legend-item--sib">
              <i /> Not taken — grey, dashed, italic
            </span>
          </div>
        )}

        <div
          className="lg-canvas"
          ref={wrapRef}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endPan}
          onPointerCancel={endPan}
        >
          <svg className="lg-svg" role="presentation">
            <defs>
              <marker id="lg-arrow" viewBox="0 0 8 8" refX="7" refY="4"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" fill="#94a3b8" />
              </marker>
              <marker id="lg-arrow-jump" viewBox="0 0 8 8" refX="7" refY="4"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" fill="#f59e0b" />
              </marker>
              <marker id="lg-arrow-ghost" viewBox="0 0 8 8" refX="7" refY="4"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L8,4 L0,8 z" fill="#c2ccd8" />
              </marker>
            </defs>
            <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
              {laid.edges.map((e) => (
                <path
                  key={`${e.from}->${e.to}`}
                  d={edgePath(e)}
                  fill="none"
                  stroke={EDGE_STROKE[e.type] || EDGE_STROKE.walk}
                  strokeWidth={e.type === "walk" ? 1.6 : 1.3}
                  strokeDasharray={EDGE_DASH[e.type]}
                  markerEnd={`url(#${e.type === "jump" ? "lg-arrow-jump" : e.type === "untaken" ? "lg-arrow-ghost" : "lg-arrow"})`}
                />
              ))}

              {/* Conditions, in the Diagnostic Map's bordered pill. Drawn as
                  their own pass so a later node's box never covers one, and
                  only for the branch that WAS taken: an untaken branch's
                  condition is already the ghost node's own label. */}
              {laid.edges.map((e) => (
                e.type === "walk" ? <EdgeLabel key={`l:${e.from}->${e.to}`} edge={e} /> : null
              ))}

              {laid.nodes.map((n) => {
                const mark = nodeMark(n, results);
                const badge = MARK_STYLE[mark] || MARK_STYLE.unmarked;
                const t = typeStyle(n);
                const isJump = n.kind === "jump";
                const isSib = n.kind === "sibling";
                const isSelected = n.id === selectedId;
                /* A narrow chip reads centred; a wide box holding a whole
                   sentence is prose, and ragged-right is the only way the
                   lines scan. The text box, not the outline, decides. */
                const wide = n.textW > 260;
                const inActiveTurn =
                  activeTurn != null && n.turns.includes(Number(activeTurn));
                /* CHANNEL 1 — what the node IS: colour AND shape, both from
                   the nid family, both the Diagnostic Map's own — drawn in
                   the map's "not visited" recipe (TraversalGraph.jsx:794-798):
                   a tint of the family colour, a stroke in it, the label
                   inked in it.

                   Which means pale can no longer be what marks a branch as
                   never walked — everything is pale now. A ghost is separated
                   on FIVE channels at once:
                     · it is GREY, not a faded version of its family colour
                       (nodePalette above): hue belongs to the path that was
                       actually walked;
                     · its outline is DASHED where a walked node's is solid —
                       the one difference visible at any zoom, in greyscale,
                       and without reading anything;
                     · half the fill tint (10% vs 20%) and a 60%-alpha stroke,
                       so the whole box sits behind the walked spine;
                     · the label is italic (CSS, .lg-node--untaken) at 85%;
                     · it carries the NOT TAKEN chip where a walked node
                       carries its turn, and neither corner badge.
                   The walked node keeps both its badges, which is itself the
                   loudest tell: a numbered disc means it is on the walk.

                   A PASSED-THROUGH node — one the walk crossed but recorded
                   no step on — is the exact opposite of a ghost and must not
                   share its treatment. It keeps its FAMILY colour and a SOLID
                   outline (it WAS on the path, and the legend teaches solid =
                   walked), and is recessed only by the lighter tint, the
                   dimmed stroke and the missing badges. Grey + dashed would
                   have said "never taken" about a node the engineer walked
                   straight over. */
                const isHybrid = n.kind === "hybrid";
                const isPassthrough = !isSib && !isHybrid && mark === "passthrough";
                const faded = isSib || isPassthrough;
                const pal = isSib ? TYPE_STYLE._default : t;
                const fill = `${pal.bg}${faded ? GHOST_FILL_A : NODE_FILL_A}`;
                const stroke = faded ? `${pal.border}${GHOST_STROKE_A}` : pal.border;
                const dash = isJump ? "5 4" : isSib ? "4 4" : undefined;
                const textFill = pal.text;
                const blockTop = -(n.chipH + n.lines.length * LINE_H) / 2;
                const spot = badgeSpots(n.shape, n.W, n.H);
                /* CHANNEL 2 — how the engineer judged it: a corner badge, so
                   it never competes with the fill for the same pixels. */
                const showMark = !isSib && !isHybrid && n.steps.length > 0;
                const no = walkNo[n.id];
                return (
                  <g
                    key={n.id}
                    transform={`translate(${n.x},${n.y})`}
                    className={`lg-node${isSib ? " lg-node--untaken" : ""}${isHybrid ? " lg-node--hybrid" : ""}`}
                    data-shape={n.shape}
                    data-kind={n.kind}
                    data-type={n.type}
                    data-mark={showMark ? mark : undefined}
                    onClick={() => selectNode(n)}
                  >
                    {/* Selection — amber offset ring, the Diagnostic Map's
                        idiom (TraversalGraph.jsx:842-844). Drawn as a white
                        halo UNDER the amber: a 2.5px ring is 1.5px at the
                        0.62 zoom fit-to-view can reach, and on the dotted
                        canvas that was easy to miss. The halo separates it
                        from both the ground and the node's own stroke, so
                        the ring reads at any scale without shouting. */}
                    {isSelected && (
                      <>
                        <ShapeOutline
                          shape={n.shape} W={n.W + 11} H={n.H + 11}
                          fill="none" stroke="#ffffff" strokeWidth={6}
                        />
                        <ShapeOutline
                          shape={n.shape} W={n.W + 11} H={n.H + 11}
                          fill="none" stroke="#f59e0b" strokeWidth={3}
                        />
                      </>
                    )}
                    {/* The turn the transcript is on — cyan ring, as the map
                        rings its current node. */}
                    {inActiveTurn && !isSelected && (
                      <ShapeOutline
                        shape={n.shape} W={n.W + 8} H={n.H + 8}
                        fill="none" stroke="#22d3ee" strokeWidth={2}
                      />
                    )}
                    <ShapeOutline
                      shape={n.shape}
                      W={n.W}
                      H={n.H}
                      fill={fill}
                      stroke={stroke}
                      strokeWidth={isJump ? 2.2 : isSelected ? 2 : faded ? 1.2 : 1.6}
                      dash={dash}
                      opacity={1}
                    />
                    {/* turn chip */}
                    {n.chipH > 0 && !isSib && (
                      <text
                        x={wide ? -n.textW / 2 : 0}
                        y={blockTop + 10}
                        textAnchor={wide ? "start" : "middle"}
                        className="lg-node-turn"
                        fill={textFill}
                        opacity={faded ? GHOST_TEXT_OP : 0.8}
                      >
                        {n.turns.map((t2) => `T${t2}`).join(" ")}
                        {isJump ? "  ·  YOUR WAY" : ""}
                        {isHybrid ? "  ·  Q&A" : ""}
                        {isHybrid && hybridMarks[n.hybrid?.turn_no]
                          ? `  ·  ${String(hybridMarks[n.hybrid.turn_no]).toUpperCase()}`
                          : ""}
                      </text>
                    )}
                    {isSib && (
                      <text
                        x={wide ? -n.textW / 2 : 0}
                        y={blockTop + 10}
                        textAnchor={wide ? "start" : "middle"}
                        className="lg-node-turn"
                        fill={textFill}
                        opacity={GHOST_TEXT_OP}
                      >
                        NOT TAKEN
                      </text>
                    )}
                    {n.lines.map((line, i) => (
                      <text
                        key={i}
                        x={wide ? -n.textW / 2 : 0}
                        y={blockTop + n.chipH + 12 + i * LINE_H}
                        textAnchor={wide ? "start" : "middle"}
                        className="lg-node-text"
                        fill={textFill}
                        opacity={faded ? GHOST_TEXT_OP : 1}
                      >
                        {line}
                      </text>
                    ))}
                    {/* walk-order badge, top-right — the Diagnostic Map's
                        numbered badge on a visited node. */}
                    {no != null && (
                      <g className="lg-node-no" transform={`translate(${spot.right[0]},${spot.right[1]})`}>
                        <circle r={8.5} fill="#3b82f6" stroke="#ffffff" strokeWidth={1.5} />
                        <text textAnchor="middle" dominantBaseline="central">{no}</text>
                      </g>
                    )}
                    {/* MARK badge, top-left. Hollow while unmarked. */}
                    {showMark && (
                      <g
                        className="lg-node-mark"
                        data-mark={mark}
                        transform={`translate(${spot.left[0]},${spot.left[1]})`}
                      >
                        <circle
                          r={8.5}
                          fill={badge.fill}
                          stroke={mark === "unmarked" ? badge.border : "#ffffff"}
                          strokeWidth={1.5}
                          strokeDasharray={mark === "unmarked" ? "2 2" : undefined}
                        />
                        {badge.glyph && (
                          <text textAnchor="middle" dominantBaseline="central">{badge.glyph}</text>
                        )}
                      </g>
                    )}
                  </g>
                );
              })}
            </g>
          </svg>

          {/* Bottom-left control bar — zoom out / percentage / zoom in /
              Fit, the Diagnostic Map's bar (TraversalGraph.jsx:1140-1160).
              Pointer events stop here: the canvas behind it pans. */}
          <div className="lg-zoom" onPointerDown={(e) => e.stopPropagation()}>
            <button type="button" onClick={() => zoomBy(1 / 1.2)} title="Zoom out"><ZoomOut size={13} /></button>
            <span className="lg-zoom-pct">{Math.round(view.k * 100)}%</span>
            <button type="button" onClick={() => zoomBy(1.2)} title="Zoom in"><ZoomIn size={13} /></button>
            <span className="lg-zoom-sep" />
            <button type="button" className="lg-zoom-fit" onClick={fitWhole} title="Fit whole trail">Fit</button>
          </div>

          {/* Trace Path stepper — walks the recorded steps in order, driving
              the same selection a click makes, so the transcript follows. */}
          {walk.length > 1 && (
            <div className="lg-trace" onPointerDown={(e) => e.stopPropagation()}>
              <span className="lg-trace-label">Trace Path</span>
              <button
                type="button"
                title="Previous step"
                disabled={traceStep <= 1}
                onClick={() => goToStep(Math.max((traceStep || 1) - 1, 1))}
              >
                <ChevronLeft size={12} />
              </button>
              <button
                type="button"
                className="lg-trace-count"
                title="Focus this step"
                onClick={() => goToStep(traceStep || 1)}
              >
                {`Step ${traceStep || 1} / ${walk.length}`}
              </button>
              <button
                type="button"
                title="Next step"
                disabled={traceStep >= walk.length}
                onClick={() => goToStep(Math.min((traceStep || 0) + 1, walk.length))}
              >
                <ChevronRight size={12} />
              </button>
            </div>
          )}
        </div>


        {/* Channel 2 — how the engineer judged it. Never a fill: the badge
            that hangs on the node's top-left corner, shown here at size. */}
        <div className="lg-legend lg-legend--marks">
          {[
            ["worked", "Worked"],
            ["ruled_out", "Ruled out"],
            ["inconclusive", "Can't tell"],
            ["unmarked", "Unmarked"],
          ].map(([k, label]) => (
            <span key={k} className="lg-legend-item">
              <svg width="15" height="15" viewBox="-7.5 -7.5 15 15" aria-hidden="true">
                <circle
                  r={6.5}
                  fill={MARK_STYLE[k].fill}
                  stroke={k === "unmarked" ? MARK_STYLE[k].border : "#ffffff"}
                  strokeWidth={1.2}
                  strokeDasharray={k === "unmarked" ? "2 2" : undefined}
                />
                {MARK_STYLE[k].glyph && (
                  <text textAnchor="middle" dominantBaseline="central" className="lg-legend-glyph">
                    {MARK_STYLE[k].glyph}
                  </text>
                )}
              </svg>
              {label}
            </span>
          ))}
          <span className="lg-legend-item">
            <svg width="15" height="15" viewBox="-7.5 -7.5 15 15" aria-hidden="true">
              <circle r={6.5} fill="#3b82f6" stroke="#ffffff" strokeWidth={1.2} />
              <text textAnchor="middle" dominantBaseline="central" className="lg-legend-glyph">1</text>
            </svg>
            Step order
          </span>
        </div>
      </div>

      {/* detail panel — beside the graph.

          Three bands, and only the middle one scrolls: the head (what this
          node is, and HOW IT IS MARKED) is pinned to the top, the mark
          controls are pinned to the bottom. The panel used to be one long
          scroller with the Worked / Ruled out / Can't tell row after the
          text, the nid pair, the NOT TAKEN flag and the sources — i.e. below
          the fold, so clicking a node answered "what is this" but never
          "what did I say about it", and marking cost a scroll. */}
      <aside className="lg-detail">
        {!selected && (
          <p className="lg-detail-empty">
            Pick a node to read it in full and mark it.
          </p>
        )}
        {selected && (
          <>
            {/* Head, in the Diagnostic Map's order: type chip first, then
                the raw id with a copy button (the string an engineer pastes
                back into the runbook, so never prettified), then the state.
                Mirrors DiagnosticMapWindow's node inspector
                (TraversalGraph.jsx:1150-1200). */}
            <div className="lg-detail-head">
              <span className="lg-detail-chips">
                <span
                  className="lg-detail-type"
                  style={{
                    background: tintOf(nodePalette(selected).bg),
                    borderColor: nodePalette(selected).border,
                    color: nodePalette(selected).text,
                  }}
                >
                  {selected.kind === "jump"
                    ? "Your own way"
                    : selected.kind === "hybrid"
                      ? "Question & answer"
                      : (TYPE_LABEL[selected.type] || selected.type || "Node")}
                </span>
                {walkNo[selected.id] != null && (
                  <span className="lg-detail-step-no">Step {walkNo[selected.id]}</span>
                )}
                {/* The mark, beside the type chip and above the fold: the
                    first thing the panel answers after "what is this". Same
                    colour and glyph as the node's corner badge, so the chip
                    and the canvas never have to be reconciled by memory. */}
                {selMark && (
                  <span
                    className={`lg-detail-mark-chip${selMark === "unmarked" ? " lg-detail-mark-chip--none" : ""}`}
                    data-mark={selMark}
                    /* The badge's BORDER colour, not its fill: the fill is
                       tuned for a 9px glyph on a disc and white on the green
                       one is only 3.77:1, which a word cannot live on. The
                       darker twin of the same hue clears 5.48:1 at worst. */
                    style={selMark === "unmarked" ? undefined : {
                      background: MARK_STYLE[selMark].border,
                      borderColor: MARK_STYLE[selMark].border,
                    }}
                  >
                    {MARK_STYLE[selMark].glyph && (
                      <b aria-hidden="true">{MARK_STYLE[selMark].glyph}</b>
                    )}
                    {MARK_LABEL[selMark] || selMark}
                  </span>
                )}
                {selected.kind === "sibling" && (
                  <span className="lg-detail-flag">NOT TAKEN</span>
                )}
              </span>
              {/* The id and the turn share a row. As two stacked blocks they
                  cost the pinned head two lines to say eleven characters
                  each, and in a narrow panel that was two of the five lines
                  the head had before the step text even started. */}
              <span className="lg-detail-meta">
                {selected.kind !== "hybrid" && (
                <span className="lg-detail-idrow">
                  <span className="lg-detail-id">{selected.nid || selected.id}</span>
                  <button
                    type="button"
                    className="lg-detail-copy"
                    title={copiedId === (selected.nid || selected.id) ? "Copied" : "Copy node id"}
                    aria-label={copiedId === (selected.nid || selected.id) ? "Node id copied" : "Copy node id"}
                    onClick={() => copyNid(selected.nid || selected.id)}
                  >
                    {copiedId === (selected.nid || selected.id)
                      ? <Check size={11} />
                      : <Copy size={11} />}
                  </button>
                </span>
                )}
                <span className="lg-detail-turn">
                  {selected.turns.length
                    ? selected.turns.map((t) => `Turn ${t}`).join(", ")
                    : "Not tied to a turn"}
                </span>
              </span>
            </div>

            {/* The body. The PANEL scrolls, not this band — see .lg-detail. */}
            <div className="lg-detail-body">
            {/* A ghost has no step behind it: no step_no, no result, nothing
                to mark. Its label and its id, and why it is here. */}
            {selected.kind === "sibling" && (
              <div className="lg-detail-untaken">
                <p className="lg-detail-text">{selected.label || selected.nid}</p>
                <p className="lg-detail-untaken-note">
                  Not taken — this branch was available at the fork above and
                  the walk went another way. There is nothing to mark on it.
                </p>
              </div>
            )}

            {/* The conversation turn, rendered by the SAME card the list
                uses — Asked / Answered, the full-answer disclosure, the
                source cards and the Helpful / Partly / Wrong marks, on the
                same maps and the same handlers. Marking here is marking
                there; the header is dropped because the panel already says
                which turn this is. */}
            {selected.kind === "hybrid" && selected.hybrid && (
              <HybridTurn
                turn={selected.hybrid}
                mark={hybridMarks[selected.hybrid.turn_no]}
                reason={hybridReasons[selected.hybrid.turn_no]}
                readOnly={readOnly || !onMarkHybrid}
                onMark={onMarkHybrid}
                onReason={onReasonHybrid}
                showHeader={false}
                confirmed={!!confirmedHybrid[selected.hybrid.turn_no]}
              />
            )}

            {selected.kind !== "sibling" && selected.kind !== "hybrid" && !selected.steps.length && (
              <p className="lg-detail-empty">
                Passed through — no step was recorded on this node.
              </p>
            )}

            {selected.steps.map((step) => {
              const full =
                step.proposed_text || step.query_text || step.edge_label || stepHeadline(step);
              /* The panel has the room the card does not, so the evidence is
                 shown open here rather than behind a disclosure. Same
                 normalizer as the list — nothing renders when the trail
                 predates these columns. */
              const evidence = stepEvidence(step);
              return (
                <div key={step.step_no} className="lg-detail-step">
                  <span className="tr-step-kind">
                    {KIND_LABEL[step.type] || step.type}
                  </span>
                  <p className="lg-detail-text">{full}</p>
                  <EvidenceBranch evidence={evidence} />
                  {step.query_text && step.proposed_text && (
                    <p className="tr-step-query">"{step.query_text}"</p>
                  )}
                  {(step.from_nid || step.to_nid) && (
                    <p className="tr-step-nid">
                      {step.from_nid && step.to_nid
                        ? `${step.from_nid} → ${step.to_nid}`
                        : step.from_nid || step.to_nid}
                    </p>
                  )}
                  <EvidenceBody evidence={evidence} />
                </div>
              );
            })}

            <NodeEdges
              node={selected}
              edges={laid.edges}
              byId={nodeById}
              onPick={(id) => {
                const n = laid.nodes.find((x) => x.id === id);
                if (!n) return;
                setSelectedId(id);
                const turn = n.turns.length ? n.turns[0] : n.steps[0]?.turn_no;
                if (turn != null) onStepClick?.(turn);
                centerOn(n);
              }}
            />
            </div>

            {/* Stuck to the bottom of the panel's one scroller (one-step node): the
                mark row is the one control a reviewer uses on every node, and
                it used to sit under however much text, evidence and neighbour
                list the node happened to carry. Still the same control, the
                same handler and the same `results` map as list mode — a mark
                made here is already applied when the user flips back to List.
                A ghost never gets here: no step, nothing to mark. */}
            {selected.steps.length > 0 && (
              <div className={`lg-detail-markbar${selected.steps.length > 1 ? " lg-detail-markbar--multi" : ""}`}>
                {selected.steps.map((step) => {
                  const marked = results[step.step_no];
                  const stepInferred = step.result_source === "inferred" && !confirmedMarks[step.step_no];
                  return (
                    <div key={step.step_no} className="lg-detail-markrow">
                      <span className="lg-detail-markrow-label">
                        {selected.steps.length > 1
                          ? (KIND_LABEL[step.type] || step.type)
                          : "Mark this step"}
                      </span>
                      {!readOnly && (
                        <div className="tr-marks">
                          {RESULTS.map(({ value, label, Icon, cls }) => {
                            const active = marked === value;
                            return (
                              <button
                                key={value}
                                type="button"
                                className={`tr-mark ${active ? cls : ""}`}
                                aria-pressed={active}
                                onClick={() => onMark?.(step.step_no, value)}
                              >
                                <Icon size={12} />
                                <span>{label}</span>
                              </button>
                            );
                          })}
                          {marked && stepInferred && (
                            <span className="tr-ai-badge" title="Suggested by AI — click a mark to confirm">AI</span>
                          )}
                        </div>
                      )}
                      {readOnly && (
                        <div className="tr-marks">
                          {marked ? (
                            <span className={`tr-mark-ro ${RESULTS.find((r) => r.value === marked)?.cls || ""}`}>
                              {RESULTS.find((r) => r.value === marked)?.label || marked}
                            </span>
                          ) : (
                            <span className="tr-mark-ro">Not marked</span>
                          )}
                          {marked && stepInferred && (
                            <span className="tr-ai-badge">AI</span>
                          )}
                        </div>
                      )}
                      {/* Same component, same props, same map as the list —
                          the panel is a second view of one mark, not a second
                          mark. Always mounted; it decides for itself whether
                          the step is ruled out. */}
                      <RuledOutNote
                        stepNo={step.step_no}
                        marked={marked}
                        storedNote={notes?.[step.step_no] ?? stepResultNote(step)}
                        readOnly={readOnly}
                        onNote={onNote}
                      />
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
